import { createLatestOnly, skipRepeats, type LatestOnly } from '../../core/queue'
import type { DocHandle, Edit, FeatureRecord, Use } from '../../generated/protocol'
import { MESSAGES } from '../../messages'
import { selectFeatureLabel } from '../../state/selectors'
import type { AppStore } from '../../state/store'
import { usesAreCurrent } from '../../timeline/uses'
import type { App } from '../createApp'
import { coreUndo } from './document'
import {
  commitBuiltEdits,
  commitEdits,
  commitReportedEdits,
  documentOf,
  refusedDuringCommand,
  showCoreError,
} from './edits'
import { notify } from './notifications'
import { withOptional } from './records'

// Timeline edits (ui.md 3.9): rollback marker, reorder, suppress, delete and rename, and the uses
// of every feature that the drop warning, "Used by" and the used marks of sketches read.

function recordsOf(app: App): readonly FeatureRecord[] {
  return documentOf(app)?.recipe.features ?? []
}

/** The set_rollback edit for marker position p: features 1 to p are active (3.9). */
function rollbackEdit(records: readonly FeatureRecord[], position: number): Edit {
  const first = records[position]
  return first === undefined
    ? { type: 'set_rollback' }
    : { type: 'set_rollback', rollback: first.id }
}

/** Moves the rollback marker to position 0 to N, labeled "Roll back". */
export async function setRollback(app: App, position: number): Promise<void> {
  if (refusedDuringCommand(app)) {
    return
  }
  await commitEdits(app, 'Roll back', [rollbackEdit(recordsOf(app), position)])
}

/** A drag of the rollback marker in progress (3.9). */
export type RollbackDrag = {
  /** The marker is over gap `position` (0 to N): the model follows it live. */
  move(position: number): void
  /** Commits the drag as one undo entry, or cancels it when the marker is back where it started. */
  drop(): Promise<void>
  /** Cancels the drag (Esc): nothing is recorded and the model is as before. */
  cancel(): Promise<void>
}

/**
 * Starts dragging the rollback marker from gap `start` (3.9): a transaction labeled "Roll back"
 * whose set_rollback follows the marker through a LatestOnly on the mutation queue, so the model
 * updates live and newer gaps replace waiting ones. Null without a document.
 */
export function startRollbackDrag(app: App, start: number): RollbackDrag | null {
  const document = documentOf(app)
  if (document === null || refusedDuringCommand(app)) {
    return null
  }
  const { documentHandle } = document
  // Gaps count the timeline as it was when the drag started; the drag reorders nothing.
  const records = document.recipe.features ?? []
  let position = start
  let ended = false
  const began = app.mutations
    .run(() => app.core.beginTransaction(documentHandle, 'Roll back'))
    .then(
      () => true,
      (error: unknown) => {
        showCoreError(app, error)
        return false
      },
    )
  const rollbacks = createLatestOnly<number, void>(
    async (gap) => {
      if (await began) {
        await app.mutations.run(() =>
          app.core.applyEdits(documentHandle, null, [rollbackEdit(records, gap)]),
        )
      }
    },
    () => undefined,
    (error) => {
      showCoreError(app, error)
    },
  )

  /** Ends the transaction after the last gap was applied: committed, or canceled. */
  async function end(commit: boolean): Promise<void> {
    ended = true
    if (!commit) {
      rollbacks.cancel()
    }
    await rollbacks.flush()
    if (!(await began)) {
      return
    }
    try {
      if (commit) {
        await app.mutations.run(() => app.core.commitTransaction(documentHandle))
      } else {
        await app.mutations.run(() => app.core.cancelTransaction(documentHandle))
      }
    } catch (error) {
      showCoreError(app, error)
    }
  }

  return {
    move(gap) {
      if (ended || gap === position) {
        return
      }
      position = gap
      rollbacks.submit(gap)
    },
    async drop() {
      if (!ended) {
        await end(position !== start)
      }
    },
    async cancel() {
      if (!ended) {
        await end(false)
      }
    },
  }
}

/** Moves a feature to a timeline index, labeled "Reorder"; its own index changes nothing. */
export async function moveFeature(app: App, featureId: string, toIndex: number): Promise<void> {
  const index = recordsOf(app).findIndex((record) => record.id === featureId)
  if (index === -1 || index === toIndex || refusedDuringCommand(app)) {
    return
  }
  await commitEdits(app, 'Reorder', [{ type: 'move_feature', id: featureId, index: toIndex }])
}

/** Suppresses or unsuppresses features in one edit. */
export async function setSuppressed(
  app: App,
  featureIds: readonly string[],
  suppressed: boolean,
): Promise<void> {
  const chosen = new Set(featureIds)
  if (!recordsOf(app).some((record) => chosen.has(record.id)) || refusedDuringCommand(app)) {
    return
  }
  const verb = suppressed ? 'Suppress' : 'Unsuppress'
  await commitBuiltEdits(app, (document) => {
    // The records as they are then, so a rename queued before this keeps its name.
    const records = (document.recipe.features ?? []).filter((record) => chosen.has(record.id))
    const [first] = records
    if (first === undefined) {
      return null
    }
    const subject =
      records.length === 1
        ? selectFeatureLabel(app.store.getState(), first.id)
        : `${String(records.length)} features`
    return {
      label: `${verb} ${subject}`,
      edits: records.map((record) => ({
        type: 'update_feature',
        // Suppressed is omitted when false (model.md).
        feature: withOptional(record, 'suppressed', suppressed ? true : undefined),
      })),
    }
  })
}

/**
 * Deletes features in one edit. When the rollback feature goes, the marker first moves to the
 * nearest later feature that stays, or to the end; "Deleted {label}." then offers Undo, unless the
 * delete raised a loss or failure notification, which offers Undo itself.
 */
export async function deleteFeatures(app: App, featureIds: readonly string[]): Promise<void> {
  const document = documentOf(app)
  if (document === null) {
    return
  }
  const doomed = new Set(featureIds)
  const records = document.recipe.features ?? []
  const deleted = records.filter((record) => doomed.has(record.id))
  const [first] = deleted
  if (first === undefined || refusedDuringCommand(app)) {
    return
  }
  const edits: Edit[] = []
  const rollback = document.recipe.rollback ?? null
  if (rollback !== null && doomed.has(rollback)) {
    const rollbackIndex = records.findIndex((record) => record.id === rollback)
    const next = records.slice(rollbackIndex + 1).find((record) => !doomed.has(record.id))
    edits.push(
      next === undefined ? { type: 'set_rollback' } : { type: 'set_rollback', rollback: next.id },
    )
  }
  for (const record of deleted) {
    edits.push({ type: 'remove_feature', id: record.id })
  }
  const subject =
    deleted.length === 1
      ? selectFeatureLabel(app.store.getState(), first.id)
      : `${String(deleted.length)} features`
  const { sent, reported } = await commitReportedEdits(app, () => ({
    label: `Delete ${subject}`,
    edits,
  }))
  // A delete that broke something says so itself, with its own Undo (3.9, 4.2).
  if (!sent || reported) {
    return
  }
  notify(app, {
    severity: 'info',
    message: MESSAGES.deleted(subject),
    code: 'deleted',
    actions: [
      {
        label: 'Undo',
        run: () => {
          void coreUndo(app)
        },
      },
    ],
  })
}

/** Renames a feature, labeled "Rename"; an empty name brings back the default label. */
export async function renameFeature(app: App, featureId: string, name: string): Promise<void> {
  if (
    !recordsOf(app).some((candidate) => candidate.id === featureId) ||
    refusedDuringCommand(app)
  ) {
    return
  }
  const trimmed = name.trim()
  await commitBuiltEdits(app, (document) => {
    // Only the name changes on the record as it is then, so a change queued before this stays.
    const record = document.recipe.features?.find((candidate) => candidate.id === featureId)
    return record === undefined
      ? null
      : {
          label: 'Rename',
          edits: [
            {
              type: 'update_feature',
              feature: withOptional(record, 'name', trimmed === '' ? undefined : trimmed),
            },
          ],
        }
  })
}

/** The document revision a `list_uses` request asks about. */
type UsesInput = { readonly documentHandle: DocHandle; readonly revision: number }

/**
 * The `list_uses` requests of each app: one at a time, the newest waiting (2.5), and none sent
 * again while the request in flight or waiting already asks about the same revision.
 */
const usesRequestsByStore = new WeakMap<AppStore, LatestOnly<UsesInput>>()

function sameRevision(first: UsesInput, second: UsesInput): boolean {
  return first.documentHandle === second.documentHandle && first.revision === second.revision
}

function usesRequestsOf(app: App): LatestOnly<UsesInput> {
  const existing = usesRequestsByStore.get(app.store)
  if (existing !== undefined) {
    return existing
  }
  const latest = createLatestOnly<UsesInput, readonly Use[]>(
    ({ documentHandle }) => app.core.listUses(documentHandle),
    (uses, { documentHandle, revision }) => {
      // Stored only while the revision it describes is still the document's.
      app.store.setState((state) =>
        state.document?.documentHandle === documentHandle && state.document.revision === revision
          ? { document: { ...state.document, uses: { revision, uses } } }
          : {},
      )
    },
    () => {
      // Uses that are not known warn about nothing (3.9); the next refresh asks again.
    },
  )
  const requests = skipRepeats(latest, sameRevision)
  usesRequestsByStore.set(app.store, requests)
  return requests
}

/**
 * Asks the core for `list_uses` when `document.uses` is older than the document's revision (3.9),
 * outside the mutation queue, since it changes nothing; resolves when the uses are current, or
 * when the request for the current revision failed (the next refresh asks again). It never
 * rejects, so callers that do not wait for it drop it with `void`.
 */
export function refreshUses(app: App): Promise<void> {
  return refreshUsesAfter(app, null)
}

/** `refreshUses` once `asked` was asked for: a revision that moved on since is asked for in turn. */
async function refreshUsesAfter(app: App, asked: UsesInput | null): Promise<void> {
  const document = documentOf(app)
  if (document === null || usesAreCurrent(app.store.getState())) {
    return
  }
  const input = { documentHandle: document.documentHandle, revision: document.revision }
  if (asked !== null && sameRevision(asked, input)) {
    // The request for this revision failed; the next refresh asks again.
    return
  }
  const requests = usesRequestsOf(app)
  requests.submit(input)
  await requests.flush()
  await refreshUsesAfter(app, input)
}
